import { forwardRef } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * Price field used inside the product form. Wraps the shadcn Input
 * with two RTL-aware tweaks:
 *
 *  1. `dir="ltr"` is forced on the native <input> so the typed decimal
 *     "19.99" reads left-to-right even when the surrounding page is
 *     Arabic / Hebrew. Merchants trained on Western price formats
 *     would otherwise see "99.19" scan in visual order and mis-enter
 *     numbers. (Same trick F-002 uses on the slug input.)
 *
 *  2. The "₪ ILS" badge lives beside the input as a sibling, not as
 *     an adornment *inside* the field, so the LTR numeric area stays
 *     clean and the badge can float at the logical `end` of the row
 *     (after the input in Arabic, before it in English).
 *
 * Parsing / validation happens in the Zod schema via
 * `parseLocalePrice` — this component is presentation-only.
 */
export interface PriceInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'dir'
> {
  /** RHF field id; used for the matching <label htmlFor>. */
  id: string;
  /** Validation error message key (already resolved by the parent). */
  errorMessage?: string | null;
  /** data-testid for the input element. */
  testId?: string;
}

/** Rendered as a non-localized symbol + ISO code. The new-shekel sign (₪)
 *  and the ISO 4217 code `ILS` are identical across locales — per
 *  design §15 we explicitly prefer Latin digits + Latin currency code
 *  over mixed-script formatting. */
const CURRENCY_BADGE_TEXT = '\u20AA ILS';

export const PriceInput = forwardRef<HTMLInputElement, PriceInputProps>(function PriceInput(
  { id, errorMessage, testId, className, ...props },
  ref,
) {
  return (
    <div className="flex items-center gap-2">
      <Input
        ref={ref}
        id={id}
        type="text"
        inputMode="decimal"
        dir="ltr"
        autoComplete="off"
        // The `text-start` keeps LTR content flush with the logical
        // start — critical because the input itself is dir="ltr" but
        // the caret and padding follow the form's visual flow.
        className={cn('text-start', className)}
        aria-invalid={errorMessage ? true : undefined}
        data-testid={testId}
        {...props}
      />
      <span
        // Badge is rendered as a sibling so it can sit at the logical
        // end of the row in every locale without bleeding into the
        // LTR numeric input. `select-none` keeps it out of the tab
        // order's "feels copyable" affordance.
        aria-hidden="true"
        className="bg-sand-200 text-sand-600 select-none whitespace-nowrap rounded-md px-2.5 py-1 text-sm font-medium"
      >
        {CURRENCY_BADGE_TEXT}
      </span>
    </div>
  );
});
